import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { LocalizationService } from '../../core/localization/localization.service';

export type NotificationKind = 'success' | 'error' | 'warning' | 'info';

export interface AppNotification {
  id: number;
  message: string;
  kind: NotificationKind;
}

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly localization = inject(LocalizationService);
  private nextId = 0;
  readonly notifications = signal<AppNotification[]>([]);

  success(message: string): void {
    this.show(message, 'success');
  }

  error(error: unknown, fallback?: string): string {
    if (typeof error === 'string') {
      this.show(error, 'error');
      return error;
    }
    const message = this.messageFor(error, fallback);
    this.show(message, 'error');
    return message;
  }

  warning(message: string): void {
    this.show(message, 'warning');
  }

  info(message: string): void {
    this.show(message, 'info');
  }

  messageFor(error: unknown, fallback?: string): string {
    if (!(error instanceof HttpErrorResponse)) {
      return this.localization.translate(
        fallback ?? 'Something went wrong while processing your request. Please try again later.',
      );
    }

    if (error.status === 0) {
      return this.localization.translate(
        'Unable to reach the server. Please check your connection and try again.',
      );
    }

    const backendMessage = this.backendMessage(error.error);
    if ((error.status === 400 || error.status === 409) && backendMessage) {
      return this.localization.translate(backendMessage);
    }

    switch (error.status) {
      case 400:
        return this.localization.translate(
          fallback ?? 'Please check the information and try again.',
        );
      case 401:
        return this.localization.translate('Your session has expired. Please sign in again.');
      case 403:
        return this.localization.translate('You do not have permission to perform this action.');
      case 404:
        return this.localization.translate(
          backendMessage ?? 'The requested information could not be found.',
        );
      default:
        return error.status >= 500
          ? this.localization.translate(
              'Something went wrong while processing your request. Please try again later.',
            )
          : this.localization.translate(
              fallback ?? 'The request could not be completed. Please try again.',
            );
    }
  }

  private show(message: string, kind: NotificationKind): void {
    if (this.notifications().some((item) => item.message === message && item.kind === kind)) {
      return;
    }
    const notification = { id: ++this.nextId, message: this.localization.translate(message), kind };
    this.notifications.update((items) => [...items, notification]);
    window.setTimeout(
      () => this.dismiss(notification.id),
      kind === 'error' || kind === 'warning' ? 6000 : 3500,
    );
  }

  dismiss(id: number): void {
    this.notifications.update((items) => items.filter((item) => item.id !== id));
  }

  private backendMessage(body: unknown): string | null {
    if (!body || typeof body !== 'object') {
      return null;
    }
    const payload = body as Record<string, unknown>;
    const validationMessage = this.firstValidationMessage(payload['errors']);
    if (validationMessage) {
      return validationMessage;
    }
    const message = payload['message'];
    return typeof message === 'string' && this.isSafe(message) ? message : null;
  }

  private firstValidationMessage(errors: unknown): string | null {
    if (!errors || typeof errors !== 'object') {
      return null;
    }
    for (const value of Object.values(errors as Record<string, unknown>)) {
      const messages = Array.isArray(value) ? value : [value];
      const message = messages.find((item) => typeof item === 'string' && this.isSafe(item));
      if (typeof message === 'string') {
        return message;
      }
    }
    return null;
  }

  private isSafe(message: string): boolean {
    const value = message.trim();
    return (
      value.length > 0 &&
      value.length <= 300 &&
      !/(HttpErrorResponse|System\.|Exception|stack trace|\bat\s+\w+\.\w+|<!DOCTYPE|\{\s*\")/i.test(
        value,
      )
    );
  }
}
